import { hasProductPackages } from '@/lib/package-utils'
import dayjs from 'dayjs'
import { getLastCompleteDate } from '@/lib/date-utils'
import { getGlobalLatestStartDate } from '@/lib/dr-utils'
import { ROAS_STANDARD_DAYS } from '@/features/roas/constants/roas-days'
import {
  getDefaultHiddenDayRoasColumnVisibility,
  getDayRoasFieldName,
  getPrevDayRoasFieldName,
} from '@/components/roi/day-roas-utils'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { createColumnVisibilityStore } from '@/components/zthd/data-table'
import { DataTableViewOptions } from '@/components/zthd/data-table-view-options'
import DataTableWithBorder from '@/components/zthd/data-table/data-table-with-border'
import {
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useColumnFilters } from '@/components/data-table-selectors'
import {
  useCountry,
  useCountry2,
  DropdownCountryCombined,
} from '@/components/DropdownCountry'
import { DropdownPid2, DropdownPid3, usePid2 } from '@/components/DropdownPid'
import DropdownYear, { useYearStore } from '@/components/DropdownYear'
import MyDatePicker, { useWeeklyDatePicker } from '@/components/MyDatePicker'
import DropdownPackageName, {
  usePackageName,
} from '@/components/DropdownPackage'
import {
  useReadableCost,
  useReadableEr,
  useReadableRevenue,
  useReadableRoas,
} from '@/hooks/auth'
import useSWR from 'swr'
import { create } from 'zustand'
import {
  columnCPI,
  columnCosts,
  columnEr,
  createAllMonthlyDayRoasColumnsWithTd3,
  columnInstalls,
  columnProfit,
  columnRevenue,
} from './columns'

import { useMemo } from 'react'
import { formatUSD, formatPercent, formatNumber } from '@/lib/format-utils'
import { cn, exportToCsv } from '@/lib/utils'
import { CreateDateColumn } from '@/components/roi/monthly/create-date-column'
import { getCostBaseline, getProfitBaseline } from '@/lib/dr-utils'

const useColumnVisibilityStore = createColumnVisibilityStore(
  getDefaultHiddenDayRoasColumnVisibility(),
  'monthly',
)

// Function to get days in month
const getDaysInMonth = (year, month) => {
  return new Date(year, month, 0).getDate()
}

// Function to generate a range of months
const getMonthRange = (year, monthsCount = 12) => {
  const months = []
  const startDate = dayjs(`${year}-01-01`)

  // Convert monthsCount to number to ensure it's treated as a number
  const count = parseInt(monthsCount, 10)

  for (let i = 0; i < count; i++) {
    const currentDate = startDate.add(i, 'month')
    const monthId = currentDate.format('YYYYMM')
    const monthYear = parseInt(year)
    const monthNum = parseInt(currentDate.format('MM'))

    months.push({
      id: monthId,
      name: currentDate.format('MMMM YYYY'),
      daysInMonth: getDaysInMonth(monthYear, monthNum),
    })
  }

  return months
}

const MonthonMonth = () => {
  return (
    <>
      <Form />
      <RenderTable />
    </>
  )
}

const Form = () => {
  return (
    <div className="flex items-center gap-4 py-4 sm:gap-2 sm:py-2 flex-wrap xs:gap-1 xs:py-1">
      <DropdownPid2 />
      <DropdownPid3 />
      <DropdownPackageName />
      <DropdownCountryCombined />
      <DropdownYear />
      <MyDatePicker
        datePickerStore={useWeeklyDatePicker}
        getMaxDate="complete"
        showMaxDate={true}
      />
    </div>
  )
}

// Modified date column with the settings button
const createDateColumn = (
  country,
  drBaselines,
  pidForBaseline,
  costBaselines,
  profitBaselines,
  packageName = null,
) => ({
  id: 'month',
  accessorFn: (row) => row.month,
  header: 'Month',
  cell: ({ row }) => (
    <CreateDateColumn
      row={row}
      country={country}
      drBaselines={drBaselines}
      pidForBaseline={pidForBaseline}
      costBaselines={costBaselines}
      profitBaselines={profitBaselines}
      packageName={packageName}
    />
  ),
})

// Helper function to get days in month as YYYYMMDD array
const getDaysInMonthArray = (monthId) => {
  const month = dayjs(monthId, 'YYYYMM')
  const daysInMonth = month.daysInMonth()
  const days = []
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(month.date(i).format('YYYYMMDD'))
  }
  return days
}

// Create a simplified version of the Goals column without edit functionality
const createSimpleGoalsColumn = (
  costBaselines,
  profitBaselines,
  country,
  pid,
) => ({
  id: 'goals',
  header: 'Target',
  size: 225, // 设置列宽为 225px（默认宽度 150px 的 1.5 倍）
  cell: ({ row }) => {
    if (!row.original || !row.original.monthId) {
      return <div className="text-right text-muted-foreground">-</div>
    }

    const costs = row.original.costs || 0
    const revenues = row.original.revenues || 0
    const profit = row.original.profit || 0
    const monthId = row.original.monthId
    const daysInMonth = row.original.daysInMonth || 30

    // 获取该月的最后一天作为代表日期
    const daysInMonthArray = getDaysInMonthArray(monthId)
    if (!daysInMonthArray || daysInMonthArray.length === 0) {
      return <div className="text-right text-muted-foreground">-</div>
    }
    const representativeDate = daysInMonthArray[daysInMonthArray.length - 1]

    // 获取 baseline 值
    const costBaseline = getCostBaseline(
      costBaselines,
      country,
      pid,
      representativeDate,
    )
    const profitBaseline = getProfitBaseline(
      profitBaselines,
      country,
      pid,
      representativeDate,
    )
    const revenueBaseline = costBaseline + profitBaseline

    // 计算完成率：实际值 / (baseline * 天数)
    const costCompletionRate =
      costBaseline > 0 && daysInMonth > 0
        ? costs / (daysInMonth * costBaseline)
        : null
    const profitCompletionRate =
      profitBaseline !== 0 && daysInMonth > 0
        ? profit / (daysInMonth * profitBaseline)
        : null
    const revenueCompletionRate =
      revenueBaseline > 0 && daysInMonth > 0
        ? revenues / (daysInMonth * revenueBaseline)
        : null

    // 决定展示哪些指标
    const hasCostBaseline = costBaseline > 0
    const hasProfitBaseline = profitBaseline !== 0
    const hasRevenueBaseline =
      revenueBaseline > 0 && hasCostBaseline && hasProfitBaseline

    // 如果没有任何 baseline，不展示任何内容
    if (!hasCostBaseline && !hasProfitBaseline) {
      return <div className="text-right text-muted-foreground">-</div>
    }

    // 构建展示的指标数组
    const indicators = []
    if (hasRevenueBaseline && revenueCompletionRate !== null) {
      indicators.push({
        label: 'R',
        rate: revenueCompletionRate,
        target: revenueBaseline * daysInMonth,
        actual: revenues,
      })
    }
    if (hasCostBaseline && costCompletionRate !== null) {
      indicators.push({
        label: 'C',
        rate: costCompletionRate,
        target: costBaseline * daysInMonth,
        actual: costs,
      })
    }
    if (hasProfitBaseline && profitCompletionRate !== null) {
      indicators.push({
        label: 'P',
        rate: profitCompletionRate,
        target: profitBaseline * daysInMonth,
        actual: profit,
      })
    }

    // 如果只有一个指标，只展示一个
    if (indicators.length === 1) {
      const indicator = indicators[0]
      const progressBarWidth = 120 // 固定宽度（像素），代表100%目标值
      // 进度条宽度基于实际值相对于目标值的比例
      // 如果超过100%，进度条会超出虚线框，但不超过150%的宽度
      const progressWidth = Math.min(
        indicator.rate * progressBarWidth,
        progressBarWidth * 1.5,
      )
      return (
        <div className="flex items-center space-x-2 w-full">
          <div className="text-right text-xs text-muted-foreground w-4">
            {indicator.label}
          </div>
          <div
            className="relative h-5"
            style={{ width: `${progressBarWidth * 1.5}px` }}
          >
            {/* 实际进度条 */}
            <div
              className={cn(
                'h-5 rounded-sm absolute top-0 left-0',
                indicator.rate >= 1 ? 'bg-green-500' : 'bg-destructive',
              )}
              style={{ width: `${progressWidth}px`, zIndex: 1 }}
            >
              <span className="absolute left-1 top-0 bottom-0 flex items-center !text-black text-xs font-medium whitespace-nowrap">
                {formatUSD(indicator.actual / daysInMonth, 0, 0)} /{' '}
                {formatUSD(indicator.target / daysInMonth, 0, 0)}
              </span>
            </div>
            {/* 虚线框作为100%基准，在上层显示 */}
            <div
              className="h-5 rounded-sm absolute top-0 left-0 border-t-2 border-b-2 border-l-2 border-r-2 border-t-dashed border-b-dashed border-l-dashed border-r-solid border-gray-400"
              style={{ width: `${progressBarWidth}px`, zIndex: 2 }}
            />
          </div>
          <div
            className={cn(
              'text-right text-xs flex items-center',
              indicator.rate >= 1 ? 'text-green-500' : 'text-destructive',
            )}
            style={{ height: '20px' }}
          >
            {formatPercent(indicator.rate)}
          </div>
        </div>
      )
    }

    // 如果有多个指标，垂直展示
    const progressBarWidth = 120 // 固定宽度（像素），代表100%目标值
    return (
      <div className="flex flex-col space-y-1 w-full">
        {indicators.map((indicator) => {
          // 进度条宽度基于实际值相对于目标值的比例
          // 如果超过100%，进度条会超出虚线框，但不超过150%的宽度
          const progressWidth = Math.min(
            indicator.rate * progressBarWidth,
            progressBarWidth * 1.5,
          )
          return (
            <div key={indicator.label} className="flex items-center space-x-2">
              <div className="text-right text-xs text-muted-foreground w-4">
                {indicator.label}
              </div>
              <div
                className="relative h-4"
                style={{ width: `${progressBarWidth * 1.5}px` }}
              >
                {/* 实际进度条 */}
                <div
                  className={cn(
                    'h-4 rounded-sm absolute top-0 left-0',
                    indicator.rate >= 1 ? 'bg-green-500' : 'bg-destructive',
                  )}
                  style={{ width: `${progressWidth}px`, zIndex: 1 }}
                >
                  <span className="absolute left-1 top-0 bottom-0 flex items-center !text-black text-xs font-medium whitespace-nowrap">
                    {formatUSD(indicator.actual / daysInMonth, 0, 0)} /{' '}
                    {formatUSD(indicator.target / daysInMonth, 0, 0)}
                  </span>
                </div>
                {/* 虚线框作为100%基准，在上层显示 */}
                <div
                  className="h-4 rounded-sm absolute top-0 left-0 border-t-2 border-b-2 border-l-2 border-r-2 border-t-dashed border-b-dashed border-l-dashed border-r-solid border-gray-400"
                  style={{ width: `${progressBarWidth}px`, zIndex: 2 }}
                />
              </div>
              <div
                className={cn(
                  'text-right text-xs flex items-center',
                  indicator.rate >= 1 ? 'text-green-500' : 'text-destructive',
                )}
                style={{ height: '16px' }}
              >
                {formatPercent(indicator.rate)}
              </div>
            </div>
          )
        })}
      </div>
    )
  },
})

const RenderTable = () => {
  const pids = usePid2((state) => state.pids)
  const country = useCountry((state) => state.value)
  const countries = useCountry2((state) => state.countries)
  const year = useYearStore((state) => state.value)

  const packageName = usePackageName((state) => state.value)
  const pid = pids.join(',')

  const readableCost = useReadableCost(pid, country)
  const readableRevenue = useReadableRevenue(pid, country)
  const readableEr = useReadableEr(pid, country)
  const readableRoas = useReadableRoas(pid, country)

  const {
    data: baselines,
    isLoading: isBaselineLoading,
    mutate: mutateBaselines,
  } = useSWR([readableEr ? '/ers/baselines' : null])
  const erBaseLine = baselines?.[country]?.[pid] ?? 0.35
  const { data: drBaselines, mutate: mutateDrBaselines } = useSWR([
    '/drs/baselines',
  ])
  const { data: costBaselines } = useSWR(['/costs/baselines'])
  const { data: profitBaselines } = useSWR(['/profits/baselines'])

  // 获取全局最新的 baseline 配置日期
  const latestBaselineDate = useMemo(() => {
    if (!drBaselines) return ''
    const dates = ROAS_STANDARD_DAYS.map((d) =>
      getGlobalLatestStartDate(drBaselines, d),
    ).filter(Boolean)
    return dates.length ? dates.reduce((a, b) => (a > b ? a : b)) : ''
  }, [drBaselines])

  const { columnVisibility, setColumnVisibility } = useColumnVisibilityStore()
  const columnFilters = useColumnFilters((state) => state.columnFilters)

  const endDateFromPicker = useWeeklyDatePicker((state) => state.endDate)
  const allowIncomplete = useWeeklyDatePicker((state) => state.allowIncomplete)

  // 计算完整数据的截止日期
  // 一般数据（costs, revenues, ers）：下午4点完整
  const lastCompleteDate = getLastCompleteDate(false)
  // ROAS数据：下午5点完整
  const lastCompleteDateForRoas = getLastCompleteDate(true)
  // 计算该年的最后一天和完整数据截止日期中的较小值（如果不允许不完整数据）
  const yearEndDate = Number(`${year}1231`)
  const endDate = allowIncomplete
    ? Math.min(yearEndDate, Number(endDateFromPicker))
    : Math.min(yearEndDate, Number(lastCompleteDate))
  const endDateForRoas = allowIncomplete
    ? Math.min(yearEndDate, Number(endDateFromPicker))
    : Math.min(yearEndDate, Number(lastCompleteDateForRoas))

  const {
    data: ers,
    isLoading: isErsLoading,
    mutate: mutateEr,
  } = useSWR([
    readableCost ? '/ers/monthly' : null,
    { pid, country, year, endDate, packageName, countries },
  ])

  const monthlyRoasParams = {
    pid,
    country,
    year,
    endDate: endDateForRoas,
    packageName,
    countries,
  }

  const { data: day0Roas, mutate: mutateDay0Roas } = useSWR([
    readableRoas ? '/drs/monthly' : null,
    { ...monthlyRoasParams, day_n: 0 },
  ])

  const { data: day3Roas, mutate: mutateDay3Roas } = useSWR([
    readableRoas ? '/drs/monthly' : null,
    { ...monthlyRoasParams, day_n: 3 },
  ])

  const { data: day7Roas, mutate: mutateDay7Roas } = useSWR([
    readableRoas ? '/drs/monthly' : null,
    { ...monthlyRoasParams, day_n: 7 },
  ])

  const { data: day14Roas, mutate: mutateDay14Roas } = useSWR([
    readableRoas ? '/drs/monthly' : null,
    { ...monthlyRoasParams, day_n: 14 },
  ])

  const { data: day30Roas, mutate: mutateDay30Roas } = useSWR([
    readableRoas ? '/drs/monthly' : null,
    { ...monthlyRoasParams, day_n: 30 },
  ])

  const { data: day45Roas, mutate: mutateDay45Roas } = useSWR([
    readableRoas ? '/drs/monthly' : null,
    { ...monthlyRoasParams, day_n: 45 },
  ])

  const { data: day60Roas, mutate: mutateDay60Roas } = useSWR([
    readableRoas ? '/drs/monthly' : null,
    { ...monthlyRoasParams, day_n: 60 },
  ])

  const { data: day90Roas, mutate: mutateDay90Roas } = useSWR([
    readableRoas ? '/drs/monthly' : null,
    { ...monthlyRoasParams, day_n: 90 },
  ])

  const { data: day120Roas, mutate: mutateDay120Roas } = useSWR([
    readableRoas ? '/drs/monthly' : null,
    { ...monthlyRoasParams, day_n: 120 },
  ])

  const { data: day150Roas, mutate: mutateDay150Roas } = useSWR([
    readableRoas ? '/drs/monthly' : null,
    { ...monthlyRoasParams, day_n: 150 },
  ])

  const { data: day180Roas, mutate: mutateDay180Roas } = useSWR([
    readableRoas ? '/drs/monthly' : null,
    { ...monthlyRoasParams, day_n: 180 },
  ])

  const dayRoasMonthlyByDay = useMemo(
    () => ({
      0: day0Roas,
      3: day3Roas,
      7: day7Roas,
      14: day14Roas,
      30: day30Roas,
      45: day45Roas,
      60: day60Roas,
      90: day90Roas,
      120: day120Roas,
      150: day150Roas,
      180: day180Roas,
    }),
    [
      day0Roas,
      day3Roas,
      day7Roas,
      day14Roas,
      day30Roas,
      day45Roas,
      day60Roas,
      day90Roas,
      day120Roas,
      day150Roas,
      day180Roas,
    ],
  )

  const {
    data: revenues,
    mutate: mutateR,
    isLoading: isRevenuesLoading,
  } = useSWR([
    readableRevenue ? '/revenues/monthly' : null,
    { pid, country, year, endDate, packageName, countries },
  ])

  const {
    data: costs,
    mutate: mutateC,
    isLoading: isCostsLoading,
  } = useSWR([
    readableCost ? '/costs/monthly' : null,
    { pid, country, year, endDate, packageName, countries },
  ])

  // Fetch goals data
  const {
    data: goals,
    mutate: mutateG,
    isLoading: isGoalsLoading,
  } = useSWR([
    readableCost ? '/costs/goalsline/summary' : null,
    { pid, country, year, packageName, countries },
  ])

  // Determine if any data is still loading
  const isLoading =
    (readableEr && isBaselineLoading) ||
    isErsLoading ||
    (readableRevenue && isRevenuesLoading) ||
    (readableCost && isCostsLoading) ||
    isGoalsLoading

  const data = useMemo(() => {
    const months = getMonthRange(year)
    const mappedData = months.map((month, idx) => {
      // Find the corresponding data for the current month
      const currentCost = costs?.find((c) => c.id === month.id) || {
        costs: 0,
        installs: 0,
        value: 0,
      }
      const currentRevenue = revenues?.find((r) => r.id === month.id) || {
        total: 0,
      }
      const currentEr = ers?.[month.id] || 0
      const currentGoal = goals?.[month.id] || 0

      // Find previous month data for comparison (or null if it's January)
      const prevMonth = idx > 0 ? months[idx - 1] : null
      const prevMonthCost = prevMonth
        ? costs?.find((c) => c.id === prevMonth.id) || {
            costs: 0,
            installs: 0,
            value: 0,
          }
        : null
      const prevMonthRevenue = prevMonth
        ? revenues?.find((r) => r.id === prevMonth.id) || { total: 0 }
        : null
      const prevMonthEr = prevMonth ? ers?.[prevMonth.id] || 0 : null
      const prevMonthGoal = (prevMonth && goals?.[prevMonth.id]) || 0

      // Extract values from the data
      const costsValue = currentCost.costs || 0
      const installs = currentCost.dnu || currentCost.installs || 0
      const revenueValue = currentRevenue.total || 0

      const daysInMonth =
        currentRevenue.daysInMonth ||
        month.daysInMonth ||
        currentCost.daysInMonth

      // Calculate derived values
      const profit = revenueValue - costsValue
      const cpi = installs > 0 ? costsValue / installs : 0

      // Previous month values (if available)
      const prevCostsValue = prevMonthCost?.costs || 0
      const prevInstalls = prevMonthCost?.dnu || prevMonthCost?.installs || 0
      const prevRevenueValue = prevMonthRevenue?.total || 0
      const prevProfit = prevRevenueValue - prevCostsValue
      const prevCpi = prevInstalls > 0 ? prevCostsValue / prevInstalls : 0
      const prevGoal = prevMonthGoal || 0

      const row = {
        month: month.name,
        monthId: month.id,
        daysInMonth: daysInMonth,

        // Add pid and country for API calls
        pid,
        country,

        // Add refresh function
        refresh: mutateG,

        // Current month values
        costs: costsValue,
        revenues: revenueValue,
        profit,
        installs,
        cpi,
        er: currentEr,
        goals: currentGoal,

        // Previous month values for comparison
        prevCosts: prevCostsValue,
        prevRevenues: prevRevenueValue,
        prevProfit,
        prevInstalls,
        prevCpi,
        prevEr: prevMonthEr,
        prevGoals: prevGoal,

        // Growth (compared to previous month)
        growth: prevRevenueValue
          ? (revenueValue - prevRevenueValue) / prevRevenueValue
          : 0,
      }

      ROAS_STANDARD_DAYS.forEach((day) => {
        const dayData = dayRoasMonthlyByDay[day]
        row[getDayRoasFieldName(day)] = dayData?.[month.id]
        row[getPrevDayRoasFieldName(day, 'monthly')] = prevMonth
          ? dayData?.[prevMonth.id]
          : null
      })

      return row
    })

    return mappedData
  }, [
    year,
    costs,
    revenues,
    ers,
    goals,
    isLoading,
    dayRoasMonthlyByDay,
  ])

  const columns = useMemo(() => {
    const pidForBaseline = pids.length === 1 ? pids[0] : null
    // 将产品子包传入标准查询，避免 Bus 继承原版标准。
    const packageNameForBaseline = hasProductPackages(pidForBaseline) && packageName !== 'total' ? packageName : null
    const rows = []
    // Use the modified date column instead of the original one
    rows.push(
      createDateColumn(
        country,
        drBaselines,
        pidForBaseline,
        costBaselines,
        profitBaselines,
        packageNameForBaseline,
      ),
    )

    if (readableCost) {
      rows.push(columnInstalls)
    }
    if (readableRevenue) {
      rows.push(columnRevenue)
    }
    if (readableCost) {
      rows.push(columnCosts(costBaselines, country, pidForBaseline))
    }
    if (readableRevenue && readableCost) {
      rows.push(columnProfit(profitBaselines, country, pidForBaseline))
    }

    if (readableEr) {
      rows.push(columnEr(erBaseLine))
    }

    if (readableRoas) {
      rows.push(
        ...createAllMonthlyDayRoasColumnsWithTd3(
          drBaselines,
          country,
          pidForBaseline,
          packageNameForBaseline,
        ),
      )
    }

    if (readableCost) {
      rows.push(columnCPI)
    }

    // Add the simplified Goals column without edit functionality
    rows.push(
      createSimpleGoalsColumn(
        costBaselines,
        profitBaselines,
        country,
        pidForBaseline,
      ),
    )

    return rows
  }, [
    readableRevenue,
    readableCost,
    erBaseLine,
    drBaselines,
    costBaselines,
    profitBaselines,
    country,
    pids,
    packageName,
    readableRoas,
  ])

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    enableMultiRowSelection: true,
    state: {
      columnFilters,
      columnVisibility,
    },
  })

  const refresh = () => [
    mutateR(),
    mutateC(),
    mutateG(),
    mutateDay0Roas(),
    mutateDay3Roas(),
    mutateDay7Roas(),
    mutateDay14Roas(),
    mutateDay30Roas(),
    mutateDay45Roas(),
    mutateDay60Roas(),
    mutateDay90Roas(),
    mutateDay120Roas(),
    mutateDay150Roas(),
    mutateDay180Roas(),
    mutateEr(),
    mutateBaselines(),
    mutateDrBaselines(),
  ]

  const handleExportCsv = () => {
    const filename = `monthly_report_${dayjs().format('YYYYMMDDHHmmss')}.csv`
    const exportData = data.map((item) => {
      const daysInMonth = item.daysInMonth || 30
      const installsDaily = daysInMonth > 0 ? item.installs / daysInMonth : 0
      const revenuesDaily = daysInMonth > 0 ? item.revenues / daysInMonth : 0
      const costsDaily = daysInMonth > 0 ? item.costs / daysInMonth : 0
      const profitDaily = daysInMonth > 0 ? item.profit / daysInMonth : 0

      const exportRow = {
        month: item.month,
        installs: formatNumber(item.installs),
        'installs (daily avg)': formatNumber(installsDaily),
        revenues: formatUSD(item.revenues, 2, 2),
        'revenues (daily avg)': formatUSD(revenuesDaily, 2, 2),
        Costs: formatUSD(item.costs, 2, 2),
        'Costs (daily avg)': formatUSD(costsDaily, 2, 2),
        Profit: formatUSD(item.profit, 2, 2),
        'Profit (daily avg)': formatUSD(profitDaily, 2, 2),
        ER: formatPercent((item.er || 0) / 100, 2, 2),
        CPI: formatUSD(item.cpi, 4, 4),
      }

      ROAS_STANDARD_DAYS.forEach((day) => {
        const exportKey = day === 0 ? 'ROAS' : `ROAS${day}`
        exportRow[exportKey] = formatPercent(item[getDayRoasFieldName(day)], 2, 2)
      })

      return exportRow
    })
    exportToCsv(exportData, filename)
  }

  // 格式化完整数据的截止日期用于显示
  const lastCompleteDateStr = dayjs(
    lastCompleteDate.toString(),
    'YYYYMMDD',
  ).format('YYYY-MM-DD')
  const lastCompleteDateForRoasStr = dayjs(
    lastCompleteDateForRoas.toString(),
    'YYYYMMDD',
  ).format('YYYY-MM-DD')

  return (
    <>
      <div className="flex items-center gap-4 py-4 sm:gap-2 sm:py-2 flex-wrap">
        <ToggleShowLine num={1} text="monthly sum" />
        <ToggleShowLine num={2} text="daily avg" />
        <ToggleShowLine num={3} text="monthly growth" />
        <ToggleShowLine num={4} text="absolute diff" />
        <Button onClick={refresh}>Refresh</Button>
        <Button onClick={handleExportCsv}>Export</Button>
        <span className="text-destructive text-nowrap xs:text-xs xs:text-wrap lg:text-base">
          Roas取的是total{latestBaselineDate && `(${latestBaselineDate})`}.
        </span>
        <DataTableViewOptions table={table} />
      </div>
      <div className="mb-2 px-1 text-sm text-muted-foreground">
        <span className="font-medium">数据说明：</span>
        已自动排除不完整日期的数据。一般数据（Revenue、Costs、ER）完整至{' '}
        <span className="font-medium">{lastCompleteDateStr}</span>
        （北京时间16:00后更新）， ROAS数据完整至{' '}
        <span className="font-medium">{lastCompleteDateForRoasStr}</span>
        （北京时间17:00后更新）。
      </div>

      <DataTableWithBorder table={table} />
    </>
  )
}

export const useShowDataStore = create((set) => ({
  line1: true,
  line2: true,
  line3: true,
  line4: false,
  toggleLine: (num) =>
    set((state) => ({ [`line${num}`]: !state[`line${num}`] })),
}))

const ToggleShowLine = ({ num, text }) => {
  const value = useShowDataStore((state) => state?.[`line${num}`])
  const toggleLine = useShowDataStore((state) => state.toggleLine)

  const handleChange = () => {
    toggleLine(num)
  }

  return (
    <div className="flex items-center space-x-2">
      <Checkbox
        id={`line${num}`}
        checked={value}
        onCheckedChange={handleChange}
      />
      <label
        htmlFor={`line${num}`}
        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
      >
        {text}
      </label>
    </div>
  )
}

export default MonthonMonth
